import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, ListGroup, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { newsAPI, attendanceAPI, leaveAPI } from '../services/api';
import { formatDateIST, formatTimeLocal } from '../utils/dateFormatter';
import { FaSignInAlt, FaSignOutAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';

const Dashboard = () => {
  const { user, isAdmin, isHR } = useAuth();
  const [news, setNews] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_pinned: false
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [newsRes, attendanceRes] = await Promise.all([
        newsAPI.getAll(0, 20),
        attendanceAPI.getToday(),
      ]);

      setNews(newsRes.data);
      setTodayAttendance(attendanceRes.data);

      if (isAdmin || isHR) {
        const leavesRes = await leaveAPI.getPendingLeaves();
        setPendingLeaves(leavesRes.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePunchIn = async () => {
    try {
      await attendanceAPI.punchIn();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Checked in successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
      fetchDashboardData();
      // Notify Navigation component to update timer
      window.dispatchEvent(new Event('attendanceUpdated'));
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unknown error';
      const isIPRestriction = error.response?.status === 403;

      Swal.fire({
        icon: isIPRestriction ? 'warning' : 'error',
        title: isIPRestriction ? 'Access Restricted' : 'Error',
        text: errorMessage,
        confirmButtonColor: '#635bff',
        html: isIPRestriction
          ? `<div style="text-align: left; padding: 10px;">
              <p style="margin-bottom: 10px;">${errorMessage}</p>
              <p style="color: #6b7280; font-size: 14px;">Please check:</p>
              <ul style="color: #6b7280; font-size: 14px; text-align: left; margin-top: 8px;">
                <li>You are connected to the office network</li>
                <li>Your VPN is active (if applicable)</li>
                <li>Contact IT support if you need assistance</li>
              </ul>
            </div>`
          : undefined
      });
    }
  };

  const handlePunchOut = async () => {
    try {
      await attendanceAPI.punchOut(null);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Checked out successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
      fetchDashboardData();
      // Notify Navigation component to update timer
      window.dispatchEvent(new Event('attendanceUpdated'));
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unknown error';
      const isIPRestriction = error.response?.status === 403;

      Swal.fire({
        icon: isIPRestriction ? 'warning' : 'error',
        title: isIPRestriction ? 'Access Restricted' : 'Error',
        text: errorMessage,
        confirmButtonColor: '#635bff',
        html: isIPRestriction
          ? `<div style="text-align: left; padding: 10px;">
              <p style="margin-bottom: 10px;">${errorMessage}</p>
              <p style="color: #6b7280; font-size: 14px;">Please check:</p>
              <ul style="color: #6b7280; font-size: 14px; text-align: left; margin-top: 8px;">
                <li>You are connected to the office network</li>
                <li>Your VPN is active (if applicable)</li>
                <li>Contact IT support if you need assistance</li>
              </ul>
            </div>`
          : undefined
      });
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      await newsAPI.create(formData);
      setShowCreateModal(false);
      setFormData({ title: '', content: '', is_pinned: false });
      fetchDashboardData();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Post created successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error creating post',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleDeletePost = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Post?',
      text: "Are you sure you want to delete this post?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#635bff',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await newsAPI.delete(id);
        fetchDashboardData();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Post has been deleted.',
          confirmButtonColor: '#635bff',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error deleting post',
          confirmButtonColor: '#635bff'
        });
      }
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: '14px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  const styles = {
    headerContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    pageTitle: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#0a0a0a',
      letterSpacing: '-0.02em',
      margin: 0
    },
    headerActions: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    checkInButton: {
      padding: '14px 32px',
      borderRadius: '10px',
      border: 'none',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      minWidth: '160px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    },
    checkInActive: {
      backgroundColor: '#635bff',
      color: 'white'
    },
    checkOutButton: {
      backgroundColor: '#ff8800',
      color: 'white'
    },
    checkInInactive: {
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      border: '1px solid #e5e7eb'
    },
    statusBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px'
    },
    feedCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      marginBottom: '16px',
      overflow: 'hidden',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
    },
    feedHeader: {
      padding: '20px 24px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    feedTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#0a0a0a',
      margin: 0
    },
    feedMeta: {
      fontSize: '13px',
      color: '#6b7280',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    feedContent: {
      padding: '24px',
      fontSize: '14px',
      lineHeight: '1.6',
      color: '#374151'
    },
    sidebarCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      padding: '20px',
      marginBottom: '16px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
    },
    sidebarTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#0a0a0a',
      marginBottom: '16px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    quickLinkButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 16px',
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      width: '100%',
      marginBottom: '8px',
      fontSize: '14px',
      color: '#374151',
      fontWeight: '500',
      textDecoration: 'none',
      transition: 'all 0.2s ease'
    },
    createButton: {
      padding: '10px 20px',
      borderRadius: '8px',
      backgroundColor: '#635bff',
      color: 'white',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#9ca3af',
      fontSize: '14px'
    },
    pinnedBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500'
    },
    deleteButton: {
      padding: '6px 12px',
      borderRadius: '6px',
      backgroundColor: 'transparent',
      color: '#ef4444',
      border: '1px solid #e5e7eb',
      fontSize: '13px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }
  };

  return (
    <>
      {/* Header with Check-in/Checkout */}
      <div style={styles.headerContainer}>
        <h1 style={styles.pageTitle}>Feed</h1>
        <div style={styles.headerActions}>
          {todayAttendance?.punch_in ? (
            <>
              <div style={{
                ...styles.statusBadge,
                backgroundColor: '#e8e5ff',
                color: '#635bff'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#635bff',
                  borderRadius: '50%',
                  display: 'inline-block'
                }}></span>
                Checked in at {formatTimeLocal(todayAttendance.punch_in)}
              </div>
              {!todayAttendance.punch_out && (
                <button
                  onClick={handlePunchOut}
                  style={{
                    padding: '14px 32px',
                    borderRadius: '10px',
                    border: 'none',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    minWidth: '160px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    backgroundColor: '#ff8800',
                    color: 'white'
                  }}
                >
                  <FaSignOutAlt size={18} />
                  Check Out
                </button>
              )}
              {todayAttendance.punch_out && (
                <div style={{
                  ...styles.statusBadge,
                  backgroundColor: '#fed7aa',
                  color: '#9a3412'
                }}>
                  Checked out at {formatTimeLocal(todayAttendance.punch_out)}
                </div>
              )}
            </>
          ) : (
            <button
              onClick={handlePunchIn}
              style={{
                padding: '14px 32px',
                borderRadius: '10px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                minWidth: '160px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#635bff',
                color: 'white'
              }}
            >
              <FaSignInAlt size={18} />
              Check In
            </button>
          )}
        </div>
      </div>

      <Row>
        {/* Sidebar */}
        <Col lg={4} className="mb-4">
          {/* Profile Card */}
          <div style={styles.sidebarCard}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#635bff',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '600',
                marginRight: '12px'
              }}>
                {user?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#0a0a0a', marginBottom: '2px' }}>
                  {user?.full_name}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  {user?.role}
                </div>
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.8' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#374151' }}>Department:</strong> {user?.department || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#374151' }}>Designation:</strong> {user?.designation || 'N/A'}
              </div>
            </div>
            <Button
              as={Link}
              to="/profile"
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '10px',
                borderRadius: '8px',
                backgroundColor: '#635bff',
                color: 'white',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none',
                textAlign: 'center',
                display: 'block'
              }}
            >
              View Profile
            </Button>
          </div>

          {/* Quick Links */}
          <div style={styles.sidebarCard}>
            <h3 style={styles.sidebarTitle}>Quick Links</h3>
            <Link to="/attendance" style={styles.quickLinkButton}>
              <span>📅</span>
              <span>Attendance</span>
            </Link>
            <Link to="/leaves" style={styles.quickLinkButton}>
              <span>✈️</span>
              <span>Apply for Leave</span>
            </Link>
            <Link to="/holidays" style={styles.quickLinkButton}>
              <span>🎉</span>
              <span>Holidays</span>
            </Link>
            <Link to="/files" style={styles.quickLinkButton}>
              <span>📁</span>
              <span>Files</span>
            </Link>
          </div>
        </Col>

        {/* Main Feed Column */}
        <Col lg={8} className="mb-4">
          {/* Admin/HR Create Post Button */}
          {(isAdmin || isHR) && (
            <div style={{ marginBottom: '20px' }}>
              <Button
                onClick={() => setShowCreateModal(true)}
                style={styles.createButton}
              >
                + Create Post
              </Button>
            </div>
          )}

          {/* Pending Leave Approvals - Admin/HR Only */}
          {(isAdmin || isHR) && pendingLeaves.length > 0 && (
            <div style={styles.feedCard}>
              <div style={{
                ...styles.feedHeader,
                backgroundColor: '#fef3c7',
                borderBottom: '1px solid #fde68a'
              }}>
                <h3 style={{
                  ...styles.feedTitle,
                  color: '#92400e'
                }}>
                  Pending Leave Approvals
                </h3>
                <Badge
                  style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  {pendingLeaves.length}
                </Badge>
              </div>
              <div style={{ padding: '16px 24px' }}>
                {pendingLeaves.slice(0, 3).map((leave) => (
                  <div
                    key={leave.id}
                    style={{
                      padding: '16px',
                      backgroundColor: '#fffbeb',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      border: '1px solid #fde68a'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0a0a0a', marginBottom: '4px' }}>
                          User #{leave.user_id} - {leave.leave_type}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {formatDateIST(leave.start_date)} - {formatDateIST(leave.end_date)}
                        </div>
                      </div>
                      <Button
                        as={Link}
                        to="/leaves"
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: '500',
                          textDecoration: 'none'
                        }}
                      >
                        Review
                      </Button>
                    </div>
                  </div>
                ))}
                {pendingLeaves.length > 3 && (
                  <div style={{ textAlign: 'center', marginTop: '12px' }}>
                    <Link
                      to="/leaves"
                      style={{
                        color: '#92400e',
                        fontSize: '13px',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}
                    >
                      View all {pendingLeaves.length} pending requests →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* News Feed */}
          {news.length === 0 ? (
            <div style={styles.feedCard}>
              <div style={styles.emptyState}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📰</div>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  No posts yet
                </div>
                <div>Check back later for company updates and announcements</div>
              </div>
            </div>
          ) : (
            news.map((item) => (
              <div key={item.id} style={styles.feedCard}>
                <div style={styles.feedHeader}>
                  <div>
                    <h3 style={styles.feedTitle}>{item.title}</h3>
                    <div style={styles.feedMeta}>
                      <span>{formatDateIST(item.created_at)}</span>
                      <span>•</span>
                      <span>{formatTimeLocal(item.created_at)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {item.is_pinned && (
                      <span style={styles.pinnedBadge}>Pinned</span>
                    )}
                    {(isAdmin || isHR) && (
                      <button
                        onClick={() => handleDeletePost(item.id)}
                        style={styles.deleteButton}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div style={styles.feedContent}>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }} />
                </div>
              </div>
            ))
          )}
        </Col>
      </Row>

      {/* Create Post Sidebar */}
      <>
        {/* Overlay */}
        {showCreateModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040,
              transition: 'opacity 0.3s ease'
            }}
            onClick={() => setShowCreateModal(false)}
          />
        )}

        {/* Sidebar */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: showCreateModal ? 0 : '-500px',
            width: '500px',
            height: '100vh',
            backgroundColor: 'white',
            boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
            zIndex: 1050,
            transition: 'right 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#e8e5ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                📝
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#0a0a0a'
              }}>
                Create Post
              </h3>
            </div>
            <button
              onClick={() => setShowCreateModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, padding: '24px' }}>
            <p style={{
              color: '#6b7280',
              fontSize: '14px',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Share your thoughts, announcements or updates with your team. Create engaging content to keep everyone informed.
            </p>

            <Form onSubmit={handleCreatePost}>
              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Title
                </Form.Label>
                <Form.Control
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '12px 14px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                  placeholder="Enter post title"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  Content
                  <span style={{ color: '#9ca3af', fontWeight: '400', fontSize: '13px' }}>(Optional)</span>
                </Form.Label>
                <Form.Control
                  required
                  as="textarea"
                  rows={8}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '12px 14px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    transition: 'border-color 0.2s ease'
                  }}
                  placeholder="Write your post content..."
                />
              </Form.Group>

              <div style={{
                backgroundColor: '#f9fafb',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <Form.Check
                  type="checkbox"
                  label="Pin this post to the top"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                  style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  margin: '8px 0 0 24px',
                  lineHeight: '1.5'
                }}>
                  Pinned posts appear at the top of the feed for maximum visibility
                </p>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    backgroundColor: '#635bff',
                    color: 'white',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Publish Post
                </button>
              </div>
            </Form>
          </div>
        </div>
      </>
    </>
  );
};

export default Dashboard;
